<template>
  <div class="instance-cases-table">
    <div v-if="loading" class="text-center p-8">
      <ProgressSpinner />
      <p class="mt-2 text-gray-600">Loading cases...</p>
    </div>

    <div v-else-if="error" class="text-center p-8 text-red-500">
      <p class="mb-1">{{ error }}</p>
      <p v-if="!tenant" class="text-sm text-gray-600">Please ensure a tenant is selected.</p>
      <Button label="Retry" @click="fetchCases" severity="secondary" :disabled="!tenant" />
    </div>

    <div v-else-if="cases && cases.length > 0">
      <DataTable 
        :value="cases" 
        :paginator="true" 
        :rows="100"
        :rowsPerPageOptions="[25, 50, 100, 200]"
        responsiveLayout="scroll"
        class="cases-table"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} cases"
      >
        <Column field="id" header="Case ID" :sortable="true"></Column>
        <Column field="status" header="Status" :sortable="true">
          <template #body="slotProps">
            <Tag :value="slotProps.data.status" :severity="getStatusSeverity(slotProps.data.status)" />
          </template>
        </Column>
        <Column field="created_by" header="Created by" :sortable="true">
          <template #body="slotProps">
            {{ slotProps.data.created_by || 'N/A' }}
          </template>
        </Column>
        <Column field="inserted_at" header="Created Date" :sortable="true">
          <template #body="slotProps">
            {{ formatDate(slotProps.data.inserted_at) }}
          </template>
        </Column>
      </DataTable>
      <div v-if="meta" class="text-sm text-gray-600 mt-2 p-2 bg-gray-50 rounded">
        Total Cases: {{ meta.total_count }}
      </div>
    </div>
    
    <div v-else class="text-center p-8 text-gray-500">
      <p>No cases found for the selected criteria.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import casesService from '../utils/casesService'; // Adjusted path

const props = defineProps({
  tenant: {
    type: String,
    required: true
  },
  startDate: {
    type: String, // Expected format YYYY-MM-DD
    default: null
  },
  endDate: {
    type: String, // Expected format YYYY-MM-DD
    default: null
  }
});

const cases = ref([]);
const meta = ref(null);
const loading = ref(false);
const error = ref(null);

const fetchCases = async () => {
  if (!props.tenant) {
    error.value = 'Tenant is required to fetch cases.';
    cases.value = [];
    meta.value = null;
    loading.value = false;
    return;
  }

  loading.value = true;
  error.value = null;
  try {
    const response = await casesService.getCases(props.tenant, props.startDate, props.endDate);
    // The Elixir backend wraps data and meta
    if (response && response.data) {
      cases.value = response.data;
      meta.value = response.meta;
    } else {
      // Handle cases where response might not be structured as expected (e.g. direct error from proxy)
      cases.value = [];
      meta.value = null;
      console.warn('Unexpected response structure from cases service:', response);
      if(response && response.error) { // If proxy returned an error object directly
         error.value = response.error.message || response.error.details || 'Unexpected error structure.';
      } else {
         error.value = 'Received an unexpected response structure from the server.';
      }
    }
  } catch (err) {
    console.error('Failed to fetch cases:', err);
    error.value = err.message || 'An unknown error occurred while fetching cases.';
    cases.value = [];
    meta.value = null;
  } finally {
    loading.value = false;
  }
};

// Watch for prop changes to refetch cases
watch(() => [props.tenant, props.startDate, props.endDate], fetchCases, { immediate: true });

// Helper to format date (assuming inserted_at is ISO string)
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
  try {
    return new Date(dateString).toLocaleDateString(undefined, options);
  } catch (e) {
    return dateString; // fallback to original string if date is invalid
  }
};

const getStatusSeverity = (status) => {
  // This should align with the statuses your backend provides
  // Example, adjust as needed:
  const lowerStatus = status ? status.toLowerCase() : '';
  if (lowerStatus.includes('completed') || lowerStatus.includes('closed')) return 'success';
  if (lowerStatus.includes('active') || lowerStatus.includes('open') || lowerStatus.includes('in progress')) return 'info';
  if (lowerStatus.includes('pending') || lowerStatus.includes('new')) return 'warning';
  if (lowerStatus.includes('error') || lowerStatus.includes('failed')) return 'danger';
  return 'secondary';
};

onMounted(() => {
  // fetchCases is called immediately by the watcher if tenant is present
  if (!props.tenant) {
    loading.value = false; // Ensure loading is false if tenant is not yet available
    error.value = 'Tenant information is not yet available.';
  }
});

</script>

<style scoped>
.instance-cases-table {
  min-height: 200px; /* Ensure it has some height during loading/error states */
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
  background-color: #f8fafc; /* Light gray for header */
  color: #374151; /* Darker text for header */
  font-weight: 600;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
  padding: 0.75rem 1rem; /* Adjust cell padding */
}

:deep(.p-tag) {
  font-size: 0.8rem;
  padding: 0.2rem 0.4rem;
}
</style> 